<template>
  <v-container
    id="dashboard"
    fluid
    tag="section"
  >
  <v-form ref="ticketType">
    <base-material-card icon="mdi-ticket">
      <slot slot="title"><a @click="eventLink()">{{event.name}}</a> / {{ticketType.name}}</slot>
      <slot slot="rightButtons" v-if="ticketType.id">
        <toggle-button text="Edit" openIcon="mdi-pencil" @toggle="showEdit" :showing="showEditToggle"/>
      </slot>
      <div v-if="showEditToggle">

        <text-field label="Name" v-model="editTicketType.name" :rules="[utils.requiredRule]">
          <slot slot="tooltip">The name of the ticket - examples include "Standard Ticket", "Free Ticket", or "Front Row Ticket"</slot> 
        </text-field>
        <text-field label="Description" v-model="editTicketType.description" >
          <slot slot="tooltip">Extra description for ticket type, for example, "Please note front row seats have limited legroom."</slot> 
        </text-field>
        <text-field label="Number Of Tickets" v-model="editTicketType.quantity" :rules="[utils.integerRule]">
          <slot slot="tooltip">The total number of tickets of this type that are avaiable.<br/>You can change this at any time, but it can't be less than the number of tickets sold.</slot> 
        </text-field>

        <text-field label="Max Tickets per Order" v-model="editTicketType.maximumPerOrder" :rules="[utils.integerRule]">
          <slot slot="tooltip">The maximum number of tickets each customer can buy at one time.</slot> 
        </text-field>

        <text-field label="Price" prefix="£" v-model="editTicketType.price" :rules="[priceRule]">
          <slot slot="tooltip">The headline price for your tickets.  <br/>You must have connected your Box Office to Stripe to sell tickets.</slot> 
        </text-field>

        <text-field label="Booking Fee" prefix="£" v-model="editTicketType.bookingFee" :rules="[priceRule]">
          <slot slot="tooltip">
            A bookng fee to be added to each ticket price.</br>You could set this to cover your Happy Tickets fee (25p)</br>
            Or also cover your Stripe fees, or any amount your like.</br> Leave blank to absorb all fees yourself.
          </slot> 
        </text-field>
        <text-field label="Product Code" v-model="editTicketType.productCode">
          <slot slot="tooltip">
            Optional. For selling merchandise where your supplier needs a code.
          </slot>           
        </text-field>

        <div class="d-flex">
          <v-switch v-model="editTicketType.showAvailability"></v-switch>
          <div class="pt-5" style="valign:middle">Show available tickets at box office? 
          <tooltip left>Would you would like to show customers the number of tickets still available when they are purchasing at the Box Office?</tooltip></div>
        </div>

        <div class="d-flex">
          <v-switch v-model="editTicketType.showTicketNumbers"></v-switch>
          <div class="pt-5" style="valign:middle">Show Tickets Numbers? 
          <tooltip left>If you don't need ticket numbers for this ticket type, you can turn them off so customers won't see them</tooltip></div>
        </div>

        <div :class="{'d-flex': $vuetify.breakpoint.smAndUp}">
          Tickets on sale
          <div class="d-flex">
            <div small class="ml-2 mr-2"> from</div> 
            <datetime input-id="start" class="mb-5" input-style="border-bottom:1px solid" v-model="onSaleFrom" type="datetime" :max-datetime="onSaleTo"></datetime>
          </div> 
          <div class="d-flex">
            <span small class="ml-2 mr-2"> to </span>
            <datetime input-id="end" class="mb-5" input-style="border-bottom:1px solid" v-model="onSaleTo" type="datetime" :min-datetime="onSaleFrom"></datetime>
          </div> 
          <div class="ml-1" style="valign:top"><tooltip left>When should tickets go on sale, and stop being sold?<br/>Leave blank to start selling tickets immediately and indefinitely.</tooltip></div>
        </div>

        <v-label>
          <div class="mt-3 mb-1">Ticket Image
            <tooltip top>
              Customers can see this when they buy tickets.
            </tooltip>
          </div>
        </v-label>
        <cld-image v-if="image" :publicId="image" width="110" crop="scale"/>
        <select-image-button :boxOffice="boxOffice" @selectImage="selectImage"/>

          <v-row class="mt-5">
            <v-col cols="auto">
              <v-btn color="success" @click="updateTicketType">Save</v-btn>
              <v-btn color="white" @click="showEditToggle = false">Cancel</v-btn>
            </v-col>
            <v-spacer/>
            <v-col cols="auto">
              <v-btn class="mr-0" color="error" @click="deleteTicketType">Delete</v-btn>
            </v-col>
          </v-row>
        </div>
        <div v-else>
          <v-row>
            <v-col cols="4">Name</v-col>
            <v-col cols="8">{{ticketType.name}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Description</v-col>
            <v-col cols="8">{{ticketType.description}}</v-col>
          </v-row>          
          <v-row>
            <v-col cols="4">Number of Tickets</v-col>
            <v-col cols="8">{{ticketType.quantity}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Max Tickets per Order</v-col>
            <v-col cols="8">{{ticketType.maximumPerOrder}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Price</v-col>
            <v-col cols="8">{{utils.priceDisplay(ticketType.price)}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Booking Fee</v-col>
            <v-col cols="8">{{utils.priceDisplay(ticketType.bookingFee)}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Product Code</v-col>
            <v-col cols="8">{{ticketType.productCode}}</v-col>
          </v-row>          
          <v-row>
            <v-col cols="4">Show Available Tickets?</v-col>
            <v-col cols="8">{{ticketType.showAvailability}}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">Show Ticket Numbers?</v-col>
            <v-col cols="8">{{ticketType.showTicketNumbers}}</v-col>
          </v-row>
          <v-row v-if="ticketType.onSaleFrom || ticketType.onSaleTo">
            <v-col cols="4">Tickets on sale</v-col>
            <v-col cols="8">from {{utils.dateToShortString(ticketType.onSaleFrom)}} to {{utils.dateToShortString(ticketType.onSaleTo)}}</v-col>
          </v-row>
          <v-row v-if="ticketType.image">
            <v-col cols="4">Ticket Image</v-col>
            <v-col cols="8"><cld-image :publicId="ticketType.image" width="110" crop="scale" /></v-col>
          </v-row>
        </div>
    </base-material-card>
  </v-form>

    <v-form ref="ticket">
      <base-material-card icon="mdi-ticket-confirmation">
        <slot slot="title">Confirmed Tickets</slot>
        <v-row class="mx-n1 underlinedRow">
          <v-col cols="3">Number</v-col>
          <v-col cols="3">Name</v-col>
          <v-col cols="3">Time</v-col>
          <v-col cols="3">Total Price</v-col>
        </v-row>
        <v-row>
          <v-col class="clickableRow">
            <v-row class="mx-n1 underlinedRow" v-for="(ticket, i) in tickets" :key="i" @click="selectItem(ticket)">
              <v-col cols="3">{{ticket.ticketNumber}}</v-col>
              <v-col cols="3">{{ticket.orderName}}</v-col>
              <v-col cols="3">{{utils.dateToShortString(ticket.eventTimeStart)}}</v-col>
              <v-col cols="3">{{utils.priceDisplay(ticket.price + ticket.bookingFee)}}</v-col>
            </v-row>
          </v-col>
        </v-row>
      </base-material-card>
    </v-form>

  </v-container>

</template>
  
<script>
  import Api from '@/services/Api.js'; 
  import Utils from '@/services/Utils.js';
  import Store from '@/services/Store.js';

  export default {
    name: 'TicketType',

    components: {
      BaseMaterialCard: () => import('@/components/base/MaterialCard'),
      Tooltip: () => import('./components/Tooltip'),
      TextField: () => import('./components/TextField'),
      ToggleButton: () => import('./components/ToggleButton'),
      SelectImageButton: () => import('./components/SelectImageButton'),
    },  

    data () {
      return {
        ticketType: {},
        eventTime: {},
        event: {},
        boxOffice: {},
        editTicketType: {},
        orders: [],
        eventTimes: [],
        user: {},
        onSaleFrom: null,
        onSaleTo: null,
        tickets: [],
        showCreateOrderToggle: false,
        image: null,
        showEditToggle: false,
      }
    },

    
    computed: {
      utils: function () {
        return Utils;
      },
    },

    created() {
      this.ticketType.id = this.$route.params.id;
      this.load();
    },
    
    methods: {

      selectImage(image) {
        this.image = image.id;
        this.editTicketType.image = image.id;
      },

      eventLink() {
        this.$router.push("/dashboard/EventDetails/" + this.event.id);
      },

      priceRule(value) {
        return Utils.priceRuleAndCheckStripe(this.boxOffice, value);
      },

      async load() {
        this.ticketType = await Api.post(this, "TicketType", "read", this.ticketType);
        this.event.id = this.ticketType.eventId;
        this.event = await Api.post(this, "Event", "read", this.event);
        this.eventTimes = await Api.post(this, "EventTime", "list", this.event);
        if (this.eventTimes && this.eventTimes.length > 0) {
          this.eventTime = this.eventTimes[0];
        }
        this.boxOffice.id = this.event.boxOfficeId;
        this.boxOffice = await Api.post(this, "BoxOffice", "read", this.boxOffice);
        Store.store(this.boxOffice, this.event);
        this.listTickets();
      },

      async listTickets() {
        this.tickets = await Api.post(this, "Ticket", "list", {ticketTypeId: this.ticketType.id, status:"confirmed"});
      },

      async updateTicketType() {
        if (! await Utils.validate(this.$refs.ticketType) ) {
          return;
        }
        this.ticketType = this.editTicketType;
        this.ticketType.price = Utils.priceToNumber(this.editTicketType.price);
        this.ticketType.bookingFee = Utils.priceToNumber(this.editTicketType.bookingFee);
        this.ticketType.onSaleFrom = Utils.dateToDateTime(this.onSaleFrom);
        this.ticketType.onSaleTo = Utils.dateToDateTime(this.onSaleTo);
        this.ticketType = await Api.post(this, "TicketType", "update", this.ticketType);
        this.showEdit(false);
      },

      showEdit(show) {
        if (show) {
          this.editTicketType = Utils.jsonCopy(this.ticketType);
          this.editTicketType.price = Utils.priceToString(this.ticketType.price);
          this.editTicketType.bookingFee = Utils.priceToString(this.ticketType.bookingFee);
          this.onSaleFrom = Utils.dateToIso(this.ticketType.onSaleFrom);
          this.onSaleTo = Utils.dateToIso(this.ticketType.onSaleTo);
          this.image = this.ticketType.image;
        } 
        this.showEditToggle = show;
      },

      selectItem(ticket) {
        this.$router.push("/Dashboard/Ticket/" + ticket.id)
      },

      async deleteTicketType() {
        await Api.post(this, "TicketType", "delete", this.ticketType);
        this.$router.push("/Dashboard/EventDetails/" + this.ticketType.eventId)
      },
    }
  }
</script>
